
import { useMemo } from 'react'

import { format } from 'date-fns'

import { useCollection } from '@/shared/firebase/hooks/useCollection'
import { HotelReservation } from '@monorepo/functions/src/types/reservations'
import { HairdressingServiceType } from '@monorepo/functions/src/types/services'


export interface CheckoutChange {
    reservationId: string
    petNames: string[]
    services: HairdressingServiceType[]
    clientName: string
    currentDate: string
    currentTime: string
    newDate: string
    newTime: string
}

export function useCheckoutChanges() {
    const today = useMemo(() => format(new Date(), 'yyyy-MM-dd'), [])
    const whereClauses = useMemo(
        () => [
            ['type', '==', 'hotel'],
            ['checkoutChangeInitialDate', '>', ''],
            ['checkOutDate', '>=', today],
        ],
        [today],
    )

    const {
        results: checkoutChangesReservations,
        updateDocument,
        isLoading: isLoadingCheckoutChanges,
    } = useCollection<HotelReservation>({
        path: 'reservations',
        where: whereClauses as any,
        orderBy: ['checkOutDate', 'asc'],
    })

    const checkoutChanges = useMemo(() => {
        return checkoutChangesReservations
            .map(reservation => {
                const checkoutChange: CheckoutChange = {
                    reservationId: reservation.id,
                    petNames: reservation.pets.map(pet => pet.name),
                    clientName: reservation.client.name,
                    currentDate: reservation.checkoutChangeInitialDate || '',
                    currentTime: reservation.checkoutChangeInitialTime || '',
                    newDate: reservation.checkOutDate || '',
                    newTime: reservation.checkOutTime || '',
                    services: [
                        ...new Set(
                            reservation.additionalServices
                                ?.filter(service => service.type === 'hairdressing')
                                .flatMap(service => service.services) || [],
                        ),
                    ],
                }
                return checkoutChange
            })
            .filter(change => change.currentDate && change.currentTime)
            .sort((a, b) => a.newDate.localeCompare(b.newDate))
    }, [checkoutChangesReservations])

    const removeCheckoutChange = async (reservationId: string) => {
        await updateDocument(reservationId, {
            checkoutChangeInitialDate: undefined,
            checkoutChangeInitialTime: undefined,
        })
    }

    return {
        checkoutChanges,
        isLoadingCheckoutChanges,
        removeCheckoutChange,
    }
}
